import { createFileRoute, useNavigate, useParams } from "@tanstack/react-router";
import { Trans, useTranslation } from "react-i18next";
import Button from "@/components/core/Buttons/Button";
import Loader from "@/components/core/Loaders/Loader";
import DeviceImage from "@/components/Offer/DeviceImage";
import OfferErrorPopup from "@/components/Offer/OfferErrorPopup";
import { PromoRadioButton } from "@/components/Offer/PromoRadioButton";
import Report from "@/components/Offer/Report";
import { useOffer } from "@/hooks/useOffer";
import { useTagManager } from "@/hooks/useTagManager";
import { requireAuthWithDevice } from "@/lib/guards";
import { useStore } from "@/store";
import { formatPrice } from "@/utils/format/price";

export const Route = createFileRoute("/$locale/$partner/offer")({
	beforeLoad: requireAuthWithDevice,
	component: Offer,
});

function Offer() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { locale, partner } = useParams({ from: "/$locale/$partner" });
	const deviceInfo = useStore((state) => state.deviceInfo);

	const {
		price,
		currency,
		selectedPromoCode,
		promos,
		hasPromos,
		error,
		isLoading,
		handlePromoClick,
		getPromoAmountBasedOnPromoType,
	} = useOffer();

	useTagManager("Step Offer");

	if (error) return <OfferErrorPopup isOpen={!!error} />;

	return (
		<>
			{isLoading || !price ? (
				<div className="absolute w-fit h-fit top-1/3 left-1/2">
					<Loader />
				</div>
			) : (
				<div className="max-w-[75rem] relative mx-auto">
					<div className="flex m-auto w-full justify-between my-16">
						<div className="w-1/2 flex justify-center items-center">
							{deviceInfo.category && deviceInfo.brand && deviceInfo.model && (
								<DeviceImage
									category={deviceInfo.category}
									brand={deviceInfo.brand}
									model={deviceInfo.model}
								/>
							)}
						</div>
						<div className="w-1/2 ml-8">
							<h1 className="font-semibold text-2xl mb-8">
								<span className="block mt-16 text-base opacity-30">{deviceInfo?.brand}</span>
								{deviceInfo.model} {deviceInfo.capacity}
							</h1>
							<div className="flex mb-14">
								<div className="pr-8">
									<p className="opacity-40 font-semibold">{t("OfferPage.The value of the device is")}</p>
									<p className="text-6xl font-bold">{`${formatPrice(currency || "EUR", price)}`}</p>
								</div>
							</div>
							{hasPromos && (
								<div>
									<p className="text-xs text-gray font-bold my-2">{t("OfferPage.ActivePromos")}</p>
									{promos?.map((promo) => (
										<PromoRadioButton
											key={promo.code}
											text={
												<Trans
													i18nKey={`OfferPage.${promo.code}`}
													values={{
														amount: getPromoAmountBasedOnPromoType(promo.type, promo.amount ?? 0),
													}}
												>
													<b>Promo Amount</b>
													<span>Promo Text</span>
												</Trans>
											}
											checked={selectedPromoCode === promo.code}
											onClick={() => handlePromoClick(promo)}
										/>
									))}
									{(promos?.length ?? 0) > 1 && (
										<p className="text-xs text-gray font-bold my-2">{t("OfferPage.Disclaimer")}</p>
									)}
								</div>
							)}
							<div className="my-8">
								<Button
									size="small"
									disabled={!price}
									label={t("Buttons.Complete Trade In")}
									onClick={() =>
										navigate({ to: "/$locale/$partner/device-receipt-confirmation", params: { locale, partner } })
									}
								/>
							</div>
						</div>
					</div>
					<Report />
				</div>
			)}
		</>
	);
}
